/**
 * React Hook for E2E Encryption Key Management
 *
 * Manages client-side encryption keys and provides encryption/decryption functions
 */

import { useState, useEffect, useCallback } from 'react';
import {
  generateKeyPair,
  encryptMessage,
  decryptMessage,
  publicKeyToString,
  publicKeyFromString,
  verifyKeyPair,
  generateDeviceId,
} from '../lib/crypto/e2e';
import {
  storeKeyPair,
  getKeyPair,
  getDeviceId,
  getUserId,
  getStoredKeyInfo,
  getDetailedKeyInfo,
  markAsRegistered,
  isRegistered,
  clearKeys,
  exportKeysForBackup,
  importKeysFromBackup,
  setSessionPassphrase,
  getSessionPassphrase,
  clearSessionPassphrase,
  hasLegacyPlaintextKeys,
  migrateLegacyKeys,
} from '../lib/crypto/storage';
import { hasEncryptedKeyPair } from '../lib/crypto/keyStorage';
import { registerPublicKey, getPublicKeys, rotateKey } from '../services/encryption';

export interface UseE2EEncryptionReturn {
  // State
  hasKeys: boolean;
  isRegistered: boolean;
  publicKey: string | null;
  deviceId: string | null;
  isInitializing: boolean;
  error: string | null;
  needsPassphrase: boolean;
  needsMigration: boolean;

  // Actions
  initializeKeys: (userId: string, passphrase: string) => Promise<void>;
  unlockKeys: (passphrase: string) => Promise<boolean>;
  registerKeys: () => Promise<void>;
  encryptForUser: (plaintext: string, recipientUserId: string) => Promise<string>;
  decrypt: (encrypted: string) => Promise<string>;
  rotateKeys: (reason?: string, passphrase?: string) => Promise<void>;
  exportBackup: (passphrase?: string) => Promise<string | null>;
  importBackup: (backupJson: string, passphrase: string) => Promise<boolean>;
  migrateKeys: (passphrase: string) => Promise<boolean>;
  clearAllKeys: () => void;
}

/**
 * Hook for managing E2E encryption
 */
export function useE2EEncryption(): UseE2EEncryptionReturn {
  const [hasKeys, setHasKeys] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsPassphrase, setNeedsPassphrase] = useState(false);
  const [needsMigration, setNeedsMigration] = useState(false);

  // Load key info on mount
  useEffect(() => {
    const info = getStoredKeyInfo();
    setHasKeys(info.hasKeys);
    setRegistered(info.isRegistered);
    setDeviceId(info.deviceId);
    setNeedsMigration(info.needsMigration);

    // Check if we need a passphrase (has keys but no session passphrase)
    const hasSessionPassphrase = getSessionPassphrase() !== null;
    setNeedsPassphrase(info.hasKeys && !hasSessionPassphrase);
  }, []);

  /**
   * Unlock existing keys with passphrase
   * Returns true if successful
   */
  const unlockKeys = useCallback(async (passphrase: string): Promise<boolean> => {
    setError(null);

    try {
      const keyPair = await getKeyPair(passphrase);

      if (!keyPair) {
        setError('Invalid passphrase or no keys found');
        return false;
      }

      // Verify keypair
      if (!verifyKeyPair(keyPair)) {
        throw new Error('Keypair verification failed');
      }

      // Set session passphrase for future operations
      setSessionPassphrase(passphrase);

      // Update state
      const info = await getDetailedKeyInfo(passphrase);
      setPublicKey(info.publicKey);
      setNeedsPassphrase(false);

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock keys');
      return false;
    }
  }, []);

  /**
   * Initialize encryption keys for a user
   * Generates new keypair and encrypts with passphrase
   */
  const initializeKeys = useCallback(async (userId: string, passphrase: string) => {
    setIsInitializing(true);
    setError(null);

    try {
      // Check if keys already exist
      const existingKeyPair = await getKeyPair(passphrase);
      if (existingKeyPair) {
        // Verify existing keypair
        if (!verifyKeyPair(existingKeyPair)) {
          throw new Error('Stored keypair verification failed');
        }

        // Set session passphrase for this session
        setSessionPassphrase(passphrase);

        const info = await getDetailedKeyInfo(passphrase);
        setHasKeys(true);
        setRegistered(info.isRegistered);
        setPublicKey(info.publicKey);
        setDeviceId(info.deviceId);
        setNeedsPassphrase(false);
        return;
      }

      // Generate new keypair
      const keyPair = generateKeyPair();
      const newDeviceId = generateDeviceId();

      // Verify new keypair
      if (!verifyKeyPair(keyPair)) {
        throw new Error('Generated keypair verification failed');
      }

      // Store keypair with encryption
      console.log('initializeKeys: Storing keypair...', { userId, deviceId: newDeviceId });
      await storeKeyPair(keyPair, newDeviceId, userId, passphrase);
      console.log('initializeKeys: Keypair stored successfully');

      // Explicitly set session passphrase for this session
      setSessionPassphrase(passphrase);
      console.log('initializeKeys: Session passphrase set');

      // Verify the keypair can be retrieved
      const retrievedKeyPair = await getKeyPair();
      console.log('initializeKeys: Can retrieve keypair after storage:', !!retrievedKeyPair);

      setHasKeys(true);
      setRegistered(false);
      setPublicKey(publicKeyToString(keyPair.publicKey));
      setDeviceId(newDeviceId);
      setNeedsPassphrase(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize keys');
      throw err;
    } finally {
      setIsInitializing(false);
    }
  }, []);

  /**
   * Register public key with server
   */
  const registerKeys = useCallback(async () => {
    setError(null);

    try {
      console.log('registerKeys: Attempting to get keypair...');
      console.log('Session passphrase available:', getSessionPassphrase() !== null);
      console.log('Has encrypted keypair in storage:', hasEncryptedKeyPair());

      const keyPair = await getKeyPair();
      const storedDeviceId = getDeviceId();
      const userId = getUserId();

      console.log('registerKeys: Retrieved values:', {
        hasKeyPair: !!keyPair,
        deviceId: storedDeviceId,
        userId: userId,
      });

      if (!keyPair || !storedDeviceId || !userId) {
        throw new Error('No keys to register. Call initializeKeys first.');
      }

      // Register with server
      await registerPublicKey(userId, storedDeviceId, keyPair.publicKey);

      // Mark as registered
      markAsRegistered();
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register keys');
      throw err;
    }
  }, []);

  /**
   * Encrypt a message for a specific user
   * Fetches recipient's public key from server
   */
  const encryptForUser = useCallback(async (plaintext: string, recipientUserId: string): Promise<string> => {
    setError(null);

    try {
      // Get recipient's public key from server
      const response = await getPublicKeys([recipientUserId]);

      if (response.keys.length === 0) {
        throw new Error(`No public key found for user ${recipientUserId}`);
      }

      // Use first device's key (in production, may need to encrypt for all devices)
      const recipientKey = publicKeyFromString(response.keys[0].publicKey);

      // Encrypt message
      return encryptMessage(plaintext, recipientKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to encrypt message');
      throw err;
    }
  }, []);

  /**
   * Decrypt a message using own private key
   */
  const decrypt = useCallback(async (encrypted: string): Promise<string> => {
    setError(null);

    try {
      const keyPair = await getKeyPair();

      if (!keyPair) {
        throw new Error('No private key available. Cannot decrypt.');
      }

      return decryptMessage(encrypted, keyPair.privateKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decrypt message');
      throw err;
    }
  }, []);

  /**
   * Rotate encryption keys
   * Generates new keypair and notifies server
   */
  const rotateKeys = useCallback(async (reason?: string, passphrase?: string) => {
    setError(null);

    try {
      const oldKeyPair = await getKeyPair(passphrase);
      const storedDeviceId = getDeviceId();
      const userId = getUserId();

      if (!oldKeyPair || !storedDeviceId || !userId) {
        throw new Error('No existing keys to rotate');
      }

      // Generate new keypair
      const newKeyPair = generateKeyPair();

      // Notify server
      await rotateKey({
        userId,
        deviceId: storedDeviceId,
        oldPublicKey: publicKeyToString(oldKeyPair.publicKey),
        newPublicKey: publicKeyToString(newKeyPair.publicKey),
        reason,
      });

      // Store new keypair with encryption
      await storeKeyPair(newKeyPair, storedDeviceId, userId, passphrase);
      markAsRegistered();

      setPublicKey(publicKeyToString(newKeyPair.publicKey));
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate keys');
      throw err;
    }
  }, []);

  /**
   * Export keys for backup
   */
  const exportBackup = useCallback(async (passphrase?: string): Promise<string | null> => {
    return await exportKeysForBackup(passphrase);
  }, []);

  /**
   * Import keys from backup
   */
  const importBackup = useCallback(async (backupJson: string, passphrase: string): Promise<boolean> => {
    const success = await importKeysFromBackup(backupJson, passphrase);
    if (success) {
      const info = await getDetailedKeyInfo(passphrase);
      setHasKeys(info.hasKeys);
      setRegistered(info.isRegistered);
      setPublicKey(info.publicKey);
      setDeviceId(info.deviceId);
      setNeedsPassphrase(false);
    }
    return success;
  }, []);

  /**
   * Migrate legacy plaintext keys to encrypted storage
   */
  const migrateKeys = useCallback(async (passphrase: string): Promise<boolean> => {
    setError(null);

    try {
      const success = await migrateLegacyKeys(passphrase);

      if (success) {
        const info = await getDetailedKeyInfo(passphrase);
        setHasKeys(info.hasKeys);
        setRegistered(info.isRegistered);
        setPublicKey(info.publicKey);
        setDeviceId(info.deviceId);
        setNeedsMigration(false);
        setNeedsPassphrase(false);
      }

      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to migrate keys');
      return false;
    }
  }, []);

  /**
   * Clear all keys (e.g., on logout)
   */
  const clearAllKeys = useCallback(() => {
    clearKeys();
    setHasKeys(false);
    setRegistered(false);
    setPublicKey(null);
    setDeviceId(null);
    setError(null);
    setNeedsPassphrase(false);
    setNeedsMigration(false);
  }, []);

  return {
    // State
    hasKeys,
    isRegistered: registered,
    publicKey,
    deviceId,
    isInitializing,
    error,
    needsPassphrase,
    needsMigration,

    // Actions
    initializeKeys,
    unlockKeys,
    registerKeys,
    encryptForUser,
    decrypt,
    rotateKeys,
    exportBackup,
    importBackup,
    migrateKeys,
    clearAllKeys,
  };
}
